import { useTranslation } from 'react-i18next';

import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { usePaymentMethodsWithIssues } from '@sola/supporter-sdk/hooks/payment-methods.ts';
import { getCommitmentsTotalDue } from '@sola/supporter-sdk/utils/commitments.ts';

export interface PaymentMethodAlertProps {
  // Determines if a simple or verbose alert is displayed.
  isSimple?: boolean;
}

export const shouldRenderPaymentMethodAlert = ({
  paymentMethodsWithIssues,
  associatedCommitments,
  isSimple = false,
}: {
  paymentMethodsWithIssues: GraphQLSupporterPaymentMethod[];
  associatedCommitments: GraphQLCommitment[];
  isSimple?: boolean;
}) => {
  // TODO: UAT didn't think this alert depended on whether the payment methods were associated with commitments or not
  // Open question with UX
  // return paymentMethodsWithIssues.length > 0;

  if (!paymentMethodsWithIssues.length) {
    return false;
  }

  if (isSimple) {
    return true;
  }

  return associatedCommitments.length > 0;
};

const VerbosePaymentMethodAlert = ({ amountDue }: { amountDue: number }) => {
  const { t } = useTranslation(['my', 'core']);
  const translationKey = 'my:paymentMethodAlert.verbose';

  return (
    <Alert
      title={t(`${translationKey}.title`)}
      message={
        amountDue
          ? t(`${translationKey}.messageWithDue`, {
              totalAmountDue: t('core:numbers.currencyCustom', {
                amount: amountDue,
              }),
            })
          : t(`${translationKey}.message`)
      }
      variant="negative"
      dismissible={false}
      action={
        // TODO: Get this more dynamically?
        <Link href="/billing/settings/">{t(`${translationKey}.linkText`)}</Link>
      }
    />
  );
};

const SimplePaymentMethodAlert = () => {
  const { t } = useTranslation('my');
  const translationKey = 'paymentMethodAlert.normal';

  return (
    <Alert
      message={t(`${translationKey}.message`)}
      variant="negative"
      dismissible={false}
    />
  );
};

export const PaymentMethodAlert: React.FC<PaymentMethodAlertProps> = ({
  isSimple = false,
}) => {
  const { paymentMethodsWithIssues, associatedCommitments } =
    usePaymentMethodsWithIssues();

  if (
    !shouldRenderPaymentMethodAlert({
      paymentMethodsWithIssues,
      associatedCommitments,
      isSimple,
    })
  ) {
    return null;
  }

  if (isSimple) {
    return <SimplePaymentMethodAlert />;
  }

  return (
    <VerbosePaymentMethodAlert
      amountDue={getCommitmentsTotalDue(
        associatedCommitments.filter(
          (commitment) => commitment.hasMissedPayment,
        ),
      )}
    />
  );
};
